import React, { useEffect, useState } from 'react'
import {
  BoxProps,
  useForm,
  useBackendMutation,
  FormProvider,
  Stack,
  LinkButton,
  Flex,
  TextInput,
  Button,
  Checkbox,
  DayMonthYearInput,
  Box,
  Text,
  Link,
  useToast,
  SelectInput,
} from '@thirstycamel/ui'
import type { UserRegisterDto } from '@thirsty-camel/hump-club/src/modules/auth/dto/UserRegisterDto'
import { REGEX_MOBILE_NUMBER, REGEX_POSTCODE } from '../../utils/regex'
import Input from '../Input'
import dayjs from 'dayjs'
import { useActions, useStore } from '../../store/hooks'
import FormError from '../FormError'
import HumpClubAddress from '../HumpClubAddress'
import { eventUA } from '../../utils/gtag'
import type UserEntity from '@thirsty-camel/hump-club/src/modules/user/user.entity'
import { useUser } from '../../hooks/useUser'
import { State } from '@thirsty-camel/hump-club/src/modules/address/address.object'
import { getStateForPostcode } from '../../utils/address'
import { GENDERS } from '../../pages/account'

interface RegisterFormValues extends Omit<UserRegisterDto, 'address'> {
  postcode: string
}

export interface RegisterFormProps extends BoxProps {
  onSuccess?: (user: UserEntity) => void
  defaultValues: { [key: string]: any }
  isInStoreSignUp?: boolean
  layoutVariant?: 'default' | 'modal'
  /* True on the tokenised confirmation link (packages/web /register/complete/[token]) — identity
   * there comes from the token, not the mobile number, so unlike the legacy phone-only link it's
   * safe to let the customer correct a mistyped mobile number. */
  allowEditMobile?: boolean
  /* Collapse the fields already captured in store behind a read-only summary, so the customer's
   * attention lands on what is still missing (gender, postcode, password). An Edit toggle reveals
   * the captured fields to correct staff typos. */
  reviewPrefill?: boolean
}

export const RegisterForm = ({
  onSuccess,
  defaultValues = {},
  isInStoreSignUp,
  layoutVariant = 'default',
  allowEditMobile,
  reviewPrefill,
  ...restProps
}: RegisterFormProps) => {
  const form = useForm<RegisterFormValues>({
    defaultValues: {
      isSubscribedToNewsletter: true,
      ...(defaultValues ? defaultValues : {}),
    },
  })
  const toast = useToast()

  const { register, setStatus } = useActions(actions => actions.auth)
  const { isFetching, error } = useStore(store => store.auth)
  const { isCamelCard } = useUser()

  /* Reset the error state when this form is mounted/unmounted. */
  useEffect(() => {
    setStatus({ error: null, isFetching: false })
    return () => setStatus({ error: null, isFetching: false })
  }, [])

  //Track and store QR code, if user came from a QR
  useEffect(() => {
    const url = new URL(window.location.href)
    const qrCode = url.searchParams.get('qr')

    if (qrCode) {
      sessionStorage.setItem('qrCode', qrCode)
    }
  }, [])

  const handleSubmit = form.handleSubmit(async values => {
    const state = getStateForPostcode(values?.postcode)
    const qrCode = sessionStorage.getItem('qrCode') || null

    if (!state) {
      form.setError('postcode', {
        type: 'api',
        message: 'Please enter a valid Australian postcode.',
      })

      return
    }

    const _values = {
      ...values,
      isSubscribedToNewsletter: values.isSubscribedToNewsletter ? true : false,
      address: {
        state: state,
        postcode: values?.postcode,
        country: 'Australia',
      },
      qrSignupCode: qrCode,
    }

    if (_values.address.state === 'SA' || _values.address.state === 'NT') {
      toast({
        title: 'Signing you up for Camel Card, this may take 10-15 seconds.',
        status: 'info',
        isClosable: true,
      })
    }

    const { user } = await register({
      ..._values,
      completeProfile: isInStoreSignUp,
    })

    if (user) {
      onSuccess && onSuccess(user)

      /* Send to Google Analytics */
      eventUA('sign_up', { event_label: 'Signed up to Hump Club', method: 'email' })

      /* Send to Facebook Pixel */
      if (typeof fbq !== 'undefined') {
        fbq?.('track', 'CompleteRegistration', {
          content_name: `${values.firstName} ${values.lastName} (${values.email})`,
          status: 'true',
        })
      }
    }
  })

  // console.log(form.watch())

  const [editingDetails, setEditingDetails] = useState(false)

  const reviewableInputs: Record<string, React.ReactElement> = {
    firstName: (
      <Input
        as={TextInput}
        isRequired
        label="First name"
        name="firstName"
        placeholder="e.g. John"
        rules={{ required: 'Please enter your first name.' }}
      />
    ),
    lastName: (
      <Input
        as={TextInput}
        isRequired
        label="Last name"
        name="lastName"
        placeholder="e.g. Smith"
        rules={{ required: 'Please enter your last name.' }}
      />
    ),
    email: (
      <Input
        as={TextInput}
        isRequired
        label="Email address"
        name="email"
        type="email"
        placeholder="e.g. john@example.com"
        rules={{ required: 'Please enter an email address.' }}
      />
    ),
    dateOfBirth: (
      <Input
        as={DayMonthYearInput}
        isRequired
        label="Date of birth"
        name="dateOfBirth"
        dateFormat="YYYY-MM-DD"
        rules={{
          required: 'Please enter your date of birth.',
          validate: value =>
            dayjs(value).add(18, 'y') <= dayjs() ||
            'You must be at least 18 years of age to proceed.',
        }}
      />
    ),
    mobilePhone: (
      <Input
        as={TextInput}
        isRequired
        label="Mobile"
        name="mobilePhone"
        type="tel"
        isReadOnly={isInStoreSignUp && !allowEditMobile}
        placeholder="e.g. 0401 234 567"
        helperText={!isCamelCard && 'This is also your Hump Club number'}
        rules={{
          required: 'Please enter your mobile number.',
          pattern: {
            value: REGEX_MOBILE_NUMBER,
            message: 'Please enter a valid Australian mobile number.',
          },
        }}
      />
    ),
  }

  const reviewableFields = ['firstName', 'lastName', 'email', 'dateOfBirth', 'mobilePhone']
  const prefilledFields = reviewableFields.filter(name => Boolean(defaultValues[name]))
  const missingFields = reviewableFields.filter(name => !defaultValues[name])
  const watched = reviewPrefill ? form.watch() : ({} as RegisterFormValues)

  /* Prefilled values aren't guaranteed to pass the client rules (the in-store DTO validates a
   * looser mobile pattern than the web form), so a hidden prefilled field can fail validation and
   * block submit with no visible message and nothing to focus. Reveal the section when that happens. */
  const hasPrefilledError = prefilledFields.some(name => Boolean(form.errors?.[name]))
  const showPrefilledInputs = editingDetails || hasPrefilledError

  return (
    <FormProvider {...form}>
      <Stack as="form" spacing={5} onSubmit={handleSubmit} {...restProps}>
        <Stack spacing={5}>
          <Input as={TextInput} name="token" type="hidden" />

          {reviewPrefill ? (
            <>
              <Text color="gray.600">
                <b>Review your details and finish signing up to activate your account.</b>
              </Text>

              {prefilledFields.length > 0 && (
                <Box
                  bg="gray.50"
                  px={5}
                  py={4}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="sm"
                >
                  <Flex justify="space-between" align="center" mb={showPrefilledInputs ? 4 : 1}>
                    <Text fontWeight="bold" color="gray.700">
                      Your details
                    </Text>
                    <Button
                      type="button"
                      variant="link"
                      variantColor="pink"
                      size="sm"
                      onClick={() => setEditingDetails(value => !value)}
                    >
                      {showPrefilledInputs ? 'Done' : 'Edit'}
                    </Button>
                  </Flex>

                  {!showPrefilledInputs && (
                    <Stack spacing={0} color="gray.600" fontSize="sm">
                      {(watched.firstName || watched.lastName) && (
                        <Text>
                          {[watched.firstName, watched.lastName].filter(Boolean).join(' ')}
                        </Text>
                      )}
                      {watched.email && <Text>{watched.email}</Text>}
                      {watched.mobilePhone && <Text>{watched.mobilePhone}</Text>}
                      {watched.dateOfBirth && (
                        <Text>{dayjs(watched.dateOfBirth).format('DD/MM/YYYY')}</Text>
                      )}
                    </Stack>
                  )}

                  <Box display={showPrefilledInputs ? 'block' : 'none'}>
                    <Stack spacing={5}>
                      {prefilledFields.map(name => (
                        <React.Fragment key={name}>{reviewableInputs[name]}</React.Fragment>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}

              <Stack spacing={5}>
                {missingFields.map(name => (
                  <React.Fragment key={name}>{reviewableInputs[name]}</React.Fragment>
                ))}

                <Input
                  as={SelectInput}
                  name="gender"
                  isRequired
                  options={GENDERS}
                  placeholder="Select a gender..."
                  label="Gender"
                  maxW="25rem"
                />

                <Input
                  as={TextInput}
                  isRequired
                  type="password"
                  name="password"
                  label="Password"
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                  _placeholder={{ fontFamily: 'heading' }}
                  helperText="Must be at least 6 characters long"
                  isReadOnly={isFetching}
                  rules={{
                    required: 'Please enter your password.',
                    minLength: { value: 6, message: 'Password must be at least 6 characters.' },
                  }}
                />

                <Input
                  as={TextInput}
                  isRequired
                  label="Postcode"
                  name="postcode"
                  type="tel"
                  placeholder="0000"
                  w="6rem"
                  maxLength={4}
                  max={4}
                  rules={{
                    required: 'Please enter your postcode.',
                    pattern: {
                      value: REGEX_POSTCODE,
                      message: 'Please enter a valid Australian postcode.',
                    },
                  }}
                />
              </Stack>
            </>
          ) : (
            <>
              {isInStoreSignUp && (
                <Text color="gray.600">
                  <b>Complete your profile to activate your account.</b>
                </Text>
              )}
              <Flex direction={['column', 'row', 'column', 'row']}>
                <Input
                  as={TextInput}
                  isRequired
                  label="First name"
                  name="firstName"
                  placeholder="e.g. John"
                  rules={{ required: 'Please enter your first name.' }}
                  mb={[5, 0, 5, 0]}
                  mr={[0, 5, 0, 5]}
                />

                <Input
                  as={TextInput}
                  isRequired
                  label="Last name"
                  name="lastName"
                  placeholder="e.g. Smith"
                  rules={{ required: 'Please enter your last name.' }}
                />
              </Flex>

              {layoutVariant === 'modal' ? (
                <Flex direction={['column', 'row']} align="flex-start">
                  <Input
                    as={TextInput}
                    isRequired
                    label="Email address"
                    name="email"
                    type="email"
                    placeholder="e.g. john@example.com"
                    rules={{ required: 'Please enter an email address.' }}
                    flex="1"
                    mr={[0, 4]}
                    mb={[4, 0]}
                  />

                  <Input
                    as={TextInput}
                    isRequired
                    label="Postcode"
                    name="postcode"
                    type="tel"
                    placeholder="0000"
                    maxLength={4}
                    max={4}
                    flex="1"
                    rules={{
                      required: 'Please enter your postcode.',
                      pattern: {
                        value: REGEX_POSTCODE,
                        message: 'Please enter a valid Australian postcode.',
                      },
                    }}
                  />
                </Flex>
              ) : (
                <Input
                  as={TextInput}
                  isRequired
                  label="Email address"
                  name="email"
                  type="email"
                  placeholder="e.g. john@example.com"
                  rules={{ required: 'Please enter an email address.' }}
                />
              )}

              {layoutVariant === 'modal' ? (
                <Flex direction={['column', 'row']} align="flex-start">
                  <Input
                    as={SelectInput}
                    name="gender"
                    isRequired
                    options={GENDERS}
                    placeholder="Select a gender..."
                    label="Gender"
                    flex="1"
                    mr={[0, 4]}
                    mb={[4, 0]}
                  />

                  <Input
                    as={DayMonthYearInput}
                    isRequired
                    label="Date of birth"
                    name="dateOfBirth"
                    dateFormat="YYYY-MM-DD"
                    flex="1"
                    rules={{
                      required: 'Please enter your date of birth.',
                      validate: value =>
                        dayjs(value).add(18, 'y') <= dayjs() ||
                        'You must be at least 18 years of age to proceed.',
                    }}
                  />
                </Flex>
              ) : (
                <>
                  <Input
                    as={SelectInput}
                    name="gender"
                    isRequired
                    options={GENDERS}
                    placeholder="Select a gender..."
                    label="Gender"
                    maxW="25rem"
                  />

                  <Input
                    as={DayMonthYearInput}
                    isRequired
                    label="Date of birth"
                    name="dateOfBirth"
                    dateFormat="YYYY-MM-DD"
                    rules={{
                      required: 'Please enter your date of birth.',
                      validate: value =>
                        dayjs(value).add(18, 'y') <= dayjs() ||
                        'You must be at least 18 years of age to proceed.',
                    }}
                  />
                </>
              )}

              <Flex
                direction={['column', 'row', 'column', 'row']}
                bg="gray.50"
                px={5}
                py={5}
                border="1px"
                borderColor="gray.200"
                borderRadius="sm"
              >
                <Input
                  as={TextInput}
                  isRequired
                  label="Mobile"
                  name="mobilePhone"
                  type="tel"
                  isReadOnly={isInStoreSignUp && !allowEditMobile}
                  placeholder="e.g. 0401 234 567"
                  helperText={!isCamelCard && 'This is also your Hump Club number'}
                  rules={{
                    required: 'Please enter your mobile number.',
                    pattern: {
                      value: REGEX_MOBILE_NUMBER,
                      message: 'Please enter a valid Australian mobile number.',
                    },
                  }}
                  mb={[5, 0, 5, 0]}
                  mr={[0, 5, 0, 5]}
                />

                <Input
                  as={TextInput}
                  isRequired
                  type="password"
                  name="password"
                  label="Password"
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                  _placeholder={{ fontFamily: 'heading' }}
                  helperText="Must be at least 6 characters long"
                  isReadOnly={isFetching}
                  rules={{
                    required: 'Please enter your password.',
                    minLength: { value: 6, message: 'Password must be at least 6 characters.' },
                  }}
                />
              </Flex>

              {layoutVariant !== 'modal' && (
                <Input
                  as={TextInput}
                  isRequired
                  label="Postcode"
                  name="postcode"
                  type="tel"
                  placeholder="0000"
                  w="6rem"
                  maxLength={4}
                  max={4}
                  rules={{
                    required: 'Please enter your postcode.',
                    pattern: {
                      value: REGEX_POSTCODE,
                      message: 'Please enter a valid Australian postcode.',
                    },
                  }}
                />
              )}
            </>
          )}

          <Box pb={0}>
            <Input as={Checkbox} isRequired name="isSubscribedToNewsletter" defaultIsChecked>
              {`Yes, I'd like to subscribe to hilarious and discount-filled newsletters and other communications (we promise we'll only email you the important stuff)`}
            </Input>
          </Box>

          <Box pb={3}>
            <Input
              as={Checkbox}
              isRequired
              name="acceptedTerms"
              rules={{ required: 'You must accept the Terms & Conditions to proceed.' }}
            >
              Yes, I agree to the{' '}
              <Link href="/page/hump-club-terms-conditions" color="pink.500" isExternal>
                Terms & Conditions
              </Link>
              <Box as="span" color="red.500" fontSize="xl">
                {' '}
                *
              </Box>
            </Input>
          </Box>
        </Stack>

        <Stack spacing={5} align="center">
          <FormError
            errors={error}
            overrides={{
              409: 'Whoops! A member with this email or mobile phone number already exists.',
            }}
          />

          <Flex align="center">
            <Button type="submit" variantColor="pink" isLoading={isFetching}>
              Join the club
            </Button>

            <LinkButton href="/login" variant="outline" variantColor="pink" ml={3}>
              Login
            </LinkButton>
          </Flex>
        </Stack>
      </Stack>
    </FormProvider>
  )
}

export default RegisterForm
